import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useJiraBoardConfig, JiraBoardColumn } from '../../hooks/useJiraBoardConfig';
import './JiraBoardConfigModal.css';

interface JiraBoardConfigModalProps {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

interface ToneOption {
  id: string;
  label: string;
  color: string;
}

const TONE_OPTIONS: ToneOption[] = [
  { id: 'progress', label: 'In Progress', color: '#60a5fa' },
  { id: 'todo', label: 'To Do', color: '#94a3b8' },
  { id: 'done', label: 'Done', color: '#4ade80' },
  { id: 'staging', label: 'Staging', color: '#2dd4bf' },
  { id: 'qa', label: 'QA', color: '#a78bfa' },
  { id: 'review', label: 'Review', color: '#fbbf24' },
  { id: 'blocked', label: 'Blocked', color: '#f87171' },
  { id: 'neutral', label: 'Neutral', color: 'rgba(255,255,255,0.45)' }
];

const TONE_BY_ID: Record<string, ToneOption> = TONE_OPTIONS.reduce(
  (acc, t) => {
    acc[t.id] = t;
    return acc;
  },
  {} as Record<string, ToneOption>
);

const DEFAULT_DRAFT: JiraBoardColumn[] = [
  { id: 'progress', title: 'In Progress', tone: 'progress', statusNames: [] },
  { id: 'todo', title: 'To Do', tone: 'todo', statusNames: [] },
  { id: 'done', title: 'Done', tone: 'done', statusNames: [] }
];

const newColumn = (): JiraBoardColumn => ({
  id: `col-${Math.random().toString(36).slice(2, 9)}`,
  title: 'New column',
  tone: 'neutral',
  statusNames: []
});

const JiraBoardConfigModal: React.FC<JiraBoardConfigModalProps> = ({ open, onClose, onSaved }) => {
  const { columns: savedColumns, statuses, loading, save, clear, refetch } = useJiraBoardConfig();
  const [draft, setDraft] = useState<JiraBoardColumn[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      refetch();
      setMessage(null);
      setSearch('');
    }
  }, [open, refetch]);

  useEffect(() => {
    if (!open) return;
    const next =
      savedColumns && savedColumns.length
        ? savedColumns.map((c) => ({ ...c, statusNames: [...c.statusNames] }))
        : DEFAULT_DRAFT.map((c) => ({ ...c, statusNames: [] }));
    setDraft(next);
    setSelectedId((prev) => (prev && next.some((c) => c.id === prev) ? prev : next[0]?.id ?? null));
  }, [open, savedColumns]);

  const selected = useMemo(
    () => draft.find((c) => c.id === selectedId) || draft[0] || null,
    [draft, selectedId]
  );

  const filteredStatuses = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return statuses;
    return statuses.filter((s) => String(s.name || '').toLowerCase().includes(q));
  }, [statuses, search]);

  const updateColumn = useCallback((id: string, patch: Partial<JiraBoardColumn>) => {
    setDraft((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  }, []);

  const addColumn = useCallback(() => {
    setDraft((prev) => {
      const col = newColumn();
      setSelectedId(col.id);
      return [...prev, col];
    });
  }, []);

  const removeColumn = useCallback((id: string) => {
    setDraft((prev) => {
      const next = prev.filter((c) => c.id !== id);
      setSelectedId((curr) => {
        if (curr !== id) return curr;
        return next[0]?.id ?? null;
      });
      return next;
    });
  }, []);

  const moveColumn = useCallback((id: string, dir: -1 | 1) => {
    setDraft((prev) => {
      const idx = prev.findIndex((c) => c.id === id);
      if (idx === -1) return prev;
      const nextIdx = idx + dir;
      if (nextIdx < 0 || nextIdx >= prev.length) return prev;
      const copy = prev.slice();
      const [item] = copy.splice(idx, 1);
      copy.splice(nextIdx, 0, item);
      return copy;
    });
  }, []);

  const toggleStatus = useCallback((statusName: string) => {
    if (!selected) return;
    const colId = selected.id;
    setDraft((prev) =>
      prev.map((c) => {
        if (c.id !== colId) return c;
        const has = c.statusNames.includes(statusName);
        return {
          ...c,
          statusNames: has
            ? c.statusNames.filter((s) => s !== statusName)
            : [...c.statusNames, statusName]
        };
      })
    );
  }, [selected]);

  const handleSave = useCallback(async () => {
    const cleaned = draft
      .map((c) => ({
        id: c.id,
        title: c.title.trim(),
        tone: c.tone,
        statusNames: c.statusNames
      }))
      .filter((c) => c.title);
    if (!cleaned.length) {
      setMessage('Add at least one column with a title.');
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      await save(cleaned);
      setMessage('Board columns saved.');
      onSaved?.();
      setTimeout(() => onClose(), 700);
    } catch (err: any) {
      setMessage(err.response?.data?.error || 'Failed to save board columns');
    } finally {
      setBusy(false);
    }
  }, [draft, save, onSaved, onClose]);

  const handleReset = useCallback(async () => {
    setBusy(true);
    setMessage(null);
    try {
      await clear();
      setDraft(DEFAULT_DRAFT.map((c) => ({ ...c, statusNames: [] })));
      setSelectedId(DEFAULT_DRAFT[0].id);
      setMessage('Reset to default. Save to apply.');
      onSaved?.();
    } catch (err: any) {
      setMessage(err.response?.data?.error || 'Failed to reset');
    } finally {
      setBusy(false);
    }
  }, [clear, onSaved]);

  if (!open) return null;

  const assignedCount = selected?.statusNames.length ?? 0;

  return (
    <div className="jira-boardcfg-overlay" onClick={onClose}>
      <div className="jira-boardcfg-modal" onClick={(e) => e.stopPropagation()}>
        <div className="jira-boardcfg-header">
          <div>
            <h2>Board columns</h2>
            <p className="jira-boardcfg-sub">
              Group Jira statuses into columns. Unmapped statuses fall back to their Jira category.
            </p>
          </div>
          <button type="button" className="jira-boardcfg-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="jira-boardcfg-body">
          {loading ? (
            <div className="jira-boardcfg-muted">Loading…</div>
          ) : (
            <div className="jira-boardcfg-split">
              {/* Left pane — column list */}
              <aside className="jira-boardcfg-side">
                <div className="jira-boardcfg-side-head">Columns</div>
                <ul className="jira-boardcfg-col-list">
                  {draft.map((col, idx) => {
                    const tone = TONE_BY_ID[col.tone] || TONE_BY_ID.neutral;
                    const isActive = selected?.id === col.id;
                    return (
                      <li
                        key={col.id}
                        className={`jira-boardcfg-col-item${isActive ? ' active' : ''}`}
                        onClick={() => setSelectedId(col.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSelectedId(col.id);
                          }
                        }}
                      >
                        <span
                          className="jira-boardcfg-col-dot"
                          style={{ background: tone.color }}
                        />
                        <span className="jira-boardcfg-col-name">{col.title || 'Untitled'}</span>
                        <span className="jira-boardcfg-col-count">
                          {col.statusNames.length}
                        </span>
                        <span className="jira-boardcfg-col-tools">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); moveColumn(col.id, -1); }}
                            disabled={busy || idx === 0}
                            aria-label="Move up"
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); moveColumn(col.id, 1); }}
                            disabled={busy || idx === draft.length - 1}
                            aria-label="Move down"
                          >
                            ↓
                          </button>
                          <button
                            type="button"
                            className="jira-boardcfg-col-remove"
                            onClick={(e) => { e.stopPropagation(); removeColumn(col.id); }}
                            disabled={busy || draft.length <= 1}
                            aria-label="Remove column"
                          >
                            ✕
                          </button>
                        </span>
                      </li>
                    );
                  })}
                </ul>
                <button
                  type="button"
                  className="jira-boardcfg-add"
                  onClick={addColumn}
                  disabled={busy}
                >
                  + Add column
                </button>
              </aside>

              {/* Right pane — selected column editor */}
              <section className="jira-boardcfg-editor">
                {selected ? (
                  <>
                    <div className="jira-boardcfg-field-row">
                      <label className="jira-boardcfg-field">
                        <span>Column title</span>
                        <input
                          type="text"
                          value={selected.title}
                          placeholder="e.g. Ready for Staging"
                          onChange={(e) => updateColumn(selected.id, { title: e.target.value })}
                          disabled={busy}
                        />
                      </label>
                    </div>

                    <div className="jira-boardcfg-field-row">
                      <div className="jira-boardcfg-field">
                        <span>Accent color</span>
                        <div className="jira-boardcfg-swatches">
                          {TONE_OPTIONS.map((t) => {
                            const isActive = selected.tone === t.id;
                            return (
                              <button
                                key={t.id}
                                type="button"
                                className={`jira-boardcfg-swatch${isActive ? ' active' : ''}`}
                                style={{ background: t.color }}
                                title={t.label}
                                aria-label={t.label}
                                onClick={() => updateColumn(selected.id, { tone: t.id })}
                                disabled={busy}
                              />
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    <div className="jira-boardcfg-status-head">
                      <div className="jira-boardcfg-status-title">
                        Statuses
                        <span className="jira-boardcfg-status-count">
                          {assignedCount} assigned · {statuses.length} total
                        </span>
                      </div>
                      <input
                        type="search"
                        className="jira-boardcfg-search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Filter statuses…"
                        disabled={busy}
                      />
                    </div>

                    {statuses.length === 0 ? (
                      <div className="jira-boardcfg-muted">
                        Jira isn&apos;t connected, so no statuses are available to assign yet. Connect
                        Jira first, then come back to assign statuses.
                      </div>
                    ) : (
                      <div className="jira-boardcfg-statuses">
                        {filteredStatuses.map((s) => {
                          const checked = selected.statusNames.includes(s.name);
                          const cat = String(s.statusCategory || '').toLowerCase();
                          const catColor =
                            cat === 'done' ? '#4ade80'
                            : cat === 'in progress' ? '#60a5fa'
                            : cat === 'to do' ? '#94a3b8'
                            : 'rgba(255,255,255,0.4)';
                          return (
                            <label
                              key={s.name}
                              className={`jira-boardcfg-status${checked ? ' checked' : ''}`}
                              title={`${s.name} · ${s.statusCategory || 'no category'}`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleStatus(s.name)}
                                disabled={busy}
                              />
                              <span
                                className="jira-boardcfg-status-dot"
                                style={{ background: catColor }}
                              />
                              <span className="jira-boardcfg-status-name">{s.name}</span>
                              <span className="jira-boardcfg-status-cat">{s.statusCategory}</span>
                            </label>
                          );
                        })}
                        {filteredStatuses.length === 0 && (
                          <div className="jira-boardcfg-status-empty">
                            No statuses match “{search}”.
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="jira-boardcfg-muted">Add a column to start editing.</div>
                )}

                {message && <div className="jira-boardcfg-message">{message}</div>}
              </section>
            </div>
          )}
        </div>

        <div className="jira-boardcfg-footer">
          <button
            type="button"
            className="jira-boardcfg-btn ghost"
            onClick={handleReset}
            disabled={busy || loading}
          >
            Reset to default
          </button>
          <div className="jira-boardcfg-footer-spacer" />
          <button type="button" className="jira-boardcfg-btn ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="jira-boardcfg-btn primary"
            onClick={handleSave}
            disabled={busy || loading}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default JiraBoardConfigModal;
